<template>
  <div class="mt-4">
    <v-data-table
      :headers="headers"
      :items="presetContentPages"
      hide-default-footer
      :items-per-page="-1"
    >
      <template #[`item.page_range`]="{ item }">
        {{ item.start_page }}〜{{ item.end_page }}
      </template>
      <template #[`item.delete`]="{ item }">
        <v-btn icon @click.stop="openDeleteDialog(item)">
          <v-icon small dark>mdi-delete</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <v-divider class="my-4" />

    <div class="mx-4">
      <div class="subtitle-1 mb-2">コンテンツ追加</div>
      <v-row>
        <v-col cols="4">
          <v-text-field
            v-model="searchKeyword"
            label="コンテンツ検索（コードまたは名前）"
            @keyup.enter="searchContents"
            clearable
          ></v-text-field>
        </v-col>
        <v-col cols="2" class="d-flex align-center">
          <v-btn small color="primary" @click="searchContents" :loading="searching">検索</v-btn>
        </v-col>
      </v-row>

      <v-data-table
        v-if="searchResults.length > 0"
        :headers="searchHeaders"
        :items="searchResults"
        hide-default-footer
        :items-per-page="10"
        dense
        class="mb-4"
      >
        <template #[`item.select`]="{ item }">
          <v-btn icon small @click.stop="selectContent(item)">
            <v-icon small>mdi-plus-circle</v-icon>
          </v-btn>
        </template>
      </v-data-table>

      <div v-if="selectedContent">
        <div class="subtitle-2 mb-2">「{{ selectedContent.name }}」の教科書ページを選択</div>
        <v-row>
          <v-col cols="6">
            <v-select
              v-model="selectedContentPageId"
              :items="contentPageOptions"
              item-text="label"
              item-value="id"
              label="教科書ページ"
            ></v-select>
          </v-col>
          <v-col cols="2" class="d-flex align-center">
            <v-btn
              small
              color="secondary"
              :disabled="!selectedContentPageId"
              :loading="adding"
              @click="addContentPage"
            >
              追加
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </div>

    <v-confirm-dialog
      ref="deleteConfirm"
      title="コンテンツ削除"
      :message="deleteMessage"
      buttonMessage="削除"
      @confirm="confirmDelete"
    />
  </div>
</template>

<script>
import VConfirmDialog from '@/components/atoms/ConfirmDialog'

export default {
  components: { VConfirmDialog },
  props: {
    presetContentPages: { type: Array, default: () => [] },
    presetId: { type: Number, default: null },
  },
  data() {
    return {
      headers: [
        { text: 'No.', align: 'center', value: 'number', width: '60px' },
        { text: 'コンテンツコード', align: 'start', value: 'content_code' },
        { text: 'コンテンツ名', align: 'start', value: 'content_name' },
        { text: '出版社', align: 'center', value: 'publisher_name' },
        { text: 'ページ範囲', align: 'center', value: 'page_range', sortable: false },
        { text: '設問数', align: 'center', value: 'question_cnt' },
        { text: '削除', align: 'center', value: 'delete', sortable: false },
      ],
      searchHeaders: [
        { text: 'コード', align: 'start', value: 'code' },
        { text: '名前', align: 'start', value: 'name' },
        { text: '教科', align: 'center', value: 'subject_name' },
        { text: '学年', align: 'center', value: 'grade_name' },
        { text: '選択', align: 'center', value: 'select', sortable: false },
      ],
      searchKeyword: '',
      searchResults: [],
      searching: false,
      selectedContent: null,
      contentPageOptions: [],
      selectedContentPageId: null,
      adding: false,
      deletingPage: null,
    }
  },
  computed: {
    deleteMessage() {
      return this.deletingPage
        ? `${this.deletingPage.content_name} (No.${this.deletingPage.number}) を削除します。よろしいですか。`
        : ''
    },
  },
  methods: {
    async searchContents() {
      if (!this.searchKeyword) return
      this.searching = true
      this.selectedContent = null
      this.contentPageOptions = []
      this.selectedContentPageId = null
      try {
        const res = await this.$axios('/api/v1/admin/contents')
        const kw = this.searchKeyword.toLowerCase()
        this.searchResults = res.data.filter(c =>
          (c.code && c.code.toLowerCase().includes(kw)) ||
          (c.name && c.name.toLowerCase().includes(kw))
        )
      } catch (e) {
        this.$toasted.global.error({ message: 'コンテンツの検索に失敗しました' })
      }
      this.searching = false
    },
    async selectContent(content) {
      try {
        const res = await this.$axios(`/api/v1/admin/contents/${content.id}`)
        this.selectedContent = res.data
        this.contentPageOptions = (res.data.content_pages || []).map(cp => ({
          id: cp.id,
          label: `${cp.publisher_name} p.${cp.start_page}-${cp.end_page}`,
        }))
        this.selectedContentPageId = null
      } catch (e) {
        this.$toasted.global.error({ message: 'コンテンツの取得に失敗しました' })
      }
    },
    async addContentPage() {
      if (!this.selectedContentPageId || !this.presetId) return
      this.adding = true
      try {
        await this.$axios.post(
          `/api/v1/admin/presets/${this.presetId}/add_content_page`,
          { content_page_id: this.selectedContentPageId }
        )
        this.$toasted.global.success({ message: 'コンテンツを追加しました' })
        this.selectedContent = null
        this.contentPageOptions = []
        this.selectedContentPageId = null
        this.searchResults = []
        this.searchKeyword = ''
        this.$emit('updated')
      } catch (e) {
        this.$toasted.global.error({ message: 'コンテンツの追加に失敗しました' })
      }
      this.adding = false
    },
    openDeleteDialog(page) {
      this.deletingPage = page
      this.$refs.deleteConfirm.open()
    },
    async confirmDelete() {
      if (!this.deletingPage) return
      try {
        await this.$axios.delete(
          `/api/v1/admin/presets/${this.presetId}/remove_content_page`,
          { params: { preset_content_page_id: this.deletingPage.id } }
        )
        this.$toasted.global.success({ message: 'コンテンツを削除しました' })
        this.$emit('updated')
      } catch (e) {
        this.$toasted.global.error({ message: 'コンテンツの削除に失敗しました' })
      }
      this.deletingPage = null
    },
  },
}
</script>

<style lang="stylus" scoped>
</style>
